import {types} from './types';

/**
 * @name get
 * @desc Gets the value at path of the object. Returns null if unresolved.
 * @param {Object} source
 * @param {String} path eg: path.path1.path2.key, omit the root object in path
 * @returns {*} Value at path, or `undefined` if no value is found
 * @memberof utils
 * @example
 * get(vehicle, 'vehicle_rates[0].charge_type')
 * get(vehicle, 'mileage_info.unlimited_mileage')
 * get(vehicle, 'code')
 */
export function get(source, path) {
  path = stringToPath(path);
  const { length } = path;
  let index = 0;
  while (source !== void 0 && source !== null && index < length) {
    source = source[path[index]];
    index++;
  }
  return index < length ? void 0 : source;
}

/**
 * @private
 * @param path
 * @returns {Array|*}
 */
function stringToPath(path) {
  const containsBracketNotation = /\[[0-9]+\]/g;
  if (path.match(containsBracketNotation)) {
    path = path.replace(containsBracketNotation, s => `.${s.substring(1, s.length - 1)}`);
  }
  return path.split('.');
}

/**
 * @name isObjectEmpty
 * @desc Checks if object is empty
 * @param {Object} obj
 * @returns {boolean}
 * @memberof utils
 */
export function isObjectEmpty(obj) {
  return !!obj && obj.constructor === Object && Object.keys(obj).length === 0;
}

/**
 * @name getObjFromStrPairs
 * @desc Takes in a query string and converts it to an object of key/value pairs
 * @param {String} qs i.e. ?a=b&c=d, #a=b&c=d, or a=b&c=d
 * @param {String} [split]
 * @returns {Object}
 * @memberof utils
 * @example
 * const obj = getObjFromStrPairs('?a=b&c=d');
 * console.log(obj); // {a:'b', c:'d'};
 */
export function getObjFromStrPairs(qs, split = '&') {
  const o = {};
  if (typeof qs === 'string') {
    if (qs.indexOf('?') === 0 || qs.indexOf('#') === 0) qs = qs.substr(1);
    qs.split(split).forEach(item => {
      const itemArr = item.indexOf('=') > 0 ? item.split('=') : null;
      if (itemArr && itemArr.length) {
        o[itemArr[0]] = itemArr[1];
      }
    });
  }
  return o;
}

/**
 * @name getQsFromObj
 * @desc Takes in a flat object and converts it to a query string
 * @param {String} obj
 * @param {String} [start]
 * @returns {String}
 * @memberof utils
 * @example
 * const qs = getQsFromObj({a:'b', c:'d'});
 * console.log(qs); // '?a=b&c=d'
 */
export function getQsFromObj(obj, start = '?') {
  if (types(obj).isObject && !isObjectEmpty(obj)) {
    return Object.keys(obj).reduce((str, key, i) => {
      const value = obj[key];
      const type = types(value);
      if (!type.isArray && !type.isFunction && !type.isObject) {
        return str + (i !== 0 ? '&' : '') + key + '=' + encodeURIComponent(value);
      } else {
        return str;
      }
    }, start);
  } else {
    return '';
  }
}
